/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  FolderOpen, Video, Image as ImageIcon, Music, Radio, FileQuestion, Play,
  Search, LayoutGrid, Grid, List, X, Eye, ExternalLink, Trash2, Filter, Tag,
  Building2, Layers
} from 'lucide-react';
import { mediaLibraryService } from '../services/mediaLibraryService';
import { serviceRequestService } from '../services/serviceRequestService';
import { accountService } from '../services/accountService';
import { subAccountService } from '../services/subAccountService';
import { formatFileSize } from '../utils/formatFileSize';
import { formatRelativeTime } from '../utils/formatRelativeTime';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import MediaCardSkeleton from '../components/MediaCardSkeleton';
import MediaViewerModal from '../components/MediaViewerModal';
import Modal from '../components/Modal';
import toastService from '../utils/toastService';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionContext';
import {
  SERVICE_REQUEST_MEDIA_FILE_TYPES,
  SERVICE_REQUEST_MEDIA_FILE_TYPE_LABELS,
} from '../constants/serviceRequests';
import { ACCOUNT_LABEL, SUB_ACCOUNT_LABEL, SERVICE_REQUEST_LABEL } from '../constants/entityLabels';

const DEFAULT_LIMIT = 15;
const SKELETON_COUNT = 15;
const SEARCH_DEBOUNCE_MS = 400;

const MEDIA_TYPE_ICONS = {
  video: Video,
  image: ImageIcon,
  audio: Music,
  session_recording: Radio,
};

const INITIAL_FILTERS = { fileType: 'all', accountUid: '', subAccountUid: '', fileName: '' };

// Case-insensitive A→Z sort of filter options by name.
const sortByName = (list) =>
  [...list].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));

/* -------------------------------------------------------------------------- */
/* Comfortable Grid Card Component                                            */
/* -------------------------------------------------------------------------- */
const ComfortableMediaCard = ({
  item,
  onOpen,
  canRemove,
  onRemove,
}) => {
  const Icon = MEDIA_TYPE_ICONS[item.fileType] || FileQuestion;
  const [previewFailed, setPreviewFailed] = useState(false);
  const isImage = item.fileType === 'image';
  const isVideoLike = item.fileType === 'video' || item.fileType === 'session_recording';
  const showRealPreview = (isImage || isVideoLike) && !previewFailed;

  return (
    <div
      onClick={() => onOpen(item)}
      className="group relative bg-base-100 border border-base-content/10 hover:border-primary/40 rounded-2xl overflow-hidden cursor-pointer transition-all duration-200 hover:-translate-y-1 hover:shadow-xl shadow-sm"
    >
      {/* Thumbnail Viewport */}
      <div className="aspect-[16/10] bg-base-200/60 relative overflow-hidden flex items-center justify-center select-none">
        {showRealPreview ? (
          isImage ? (
            <img
              src={item.fileUrl}
              alt={item.fileName}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              onError={() => setPreviewFailed(true)}
            />
          ) : (
            <>
              <video
                src={item.fileUrl}
                muted
                preload="metadata"
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                onError={() => setPreviewFailed(true)}
              />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/10 group-hover:bg-black/25 transition-colors">
                <div className="w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform">
                  <Play size={16} className="fill-white ml-0.5" />
                </div>
              </div>
            </>
          )
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 text-primary/80">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shadow-inner">
              <Icon size={24} className="text-primary" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-wider text-base-content/40">
              {item.fileType}
            </span>
          </div>
        )}

        {/* Top Badges Overlay */}
        <div className="absolute top-2 right-2 flex items-center gap-1 pointer-events-none z-10">
          <span className="px-2 py-0.5 rounded-md bg-black/60 backdrop-blur-md text-white text-[10px] font-black uppercase tracking-wider shadow-sm">
            {item.fileType === 'session_recording' ? 'Session' : (SERVICE_REQUEST_MEDIA_FILE_TYPE_LABELS[item.fileType] || item.fileType)}
          </span>
        </div>

        {/* Quick Action Ribbon — visible by default (dimmed), full opacity on hover/keyboard focus */}
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute bottom-2 left-2 right-2 flex items-center justify-center gap-1.5 opacity-80 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-200 z-10"
        >
          <div className="tooltip tooltip-top tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap" data-tip="Preview media">
            <button
              type="button"
              onClick={() => onOpen(item)}
              className="p-1.5 rounded-lg bg-base-100/95 hover:bg-primary hover:text-primary-content text-base-content shadow-md backdrop-blur-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
            >
              <Eye size={14} />
            </button>
          </div>
          {item.serviceRequestUid && (
            <div className="tooltip tooltip-top tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap" data-tip={`Open ${SERVICE_REQUEST_LABEL.singular}`}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(`/service-requests/${item.serviceRequestUid}`, '_blank', 'noopener,noreferrer');
                }}
                className="p-1.5 rounded-lg bg-base-100/95 hover:bg-primary hover:text-primary-content text-base-content shadow-md backdrop-blur-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
              >
                <ExternalLink size={14} />
              </button>
            </div>
          )}
          {canRemove && (
            <div className="tooltip tooltip-top tooltip-error before:text-xs before:font-semibold before:whitespace-nowrap" data-tip="Remove media">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onRemove(item); }}
                className="p-1.5 rounded-lg bg-base-100/95 hover:bg-error hover:text-error-content text-base-content shadow-md backdrop-blur-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error focus-visible:ring-offset-1"
              >
                <Trash2 size={14} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Card Info Details */}
      <div className="p-3.5 space-y-1.5">
        <div className="tooltip tooltip-bottom tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap block w-full" data-tip={item.fileName}>
          <p className="text-xs sm:text-sm font-bold text-base-content truncate">
            {item.fileName}
          </p>
        </div>

        <div className="flex items-center justify-between text-[11px] text-base-content/50 font-medium">
          <span className="font-bold text-base-content/70">{formatFileSize(item.fileSize)}</span>
          <span>{formatRelativeTime(item.createdAt)}</span>
        </div>

        <div className="pt-2 border-t border-base-content/5 flex items-center justify-between gap-2">
          {item.requestNumber ? (
            <div className="tooltip tooltip-top tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap" data-tip={`View ${SERVICE_REQUEST_LABEL.singular} ${item.requestNumber}`}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(`/service-requests/${item.serviceRequestUid}`, '_blank', 'noopener,noreferrer');
                }}
                className="inline-flex items-center gap-1 text-[11px] font-bold text-primary hover:underline truncate max-w-[150px]"
              >
                <span>{item.requestNumber}</span>
                <ExternalLink size={10} className="flex-shrink-0" />
              </button>
            </div>
          ) : (
            <span className="text-[10px] text-base-content/30 italic">No {SERVICE_REQUEST_LABEL.singular} #</span>
          )}
        </div>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Compact Grid Card Component                                                */
/* -------------------------------------------------------------------------- */
const CompactMediaCard = ({
  item,
  onOpen,
  canRemove,
  onRemove,
}) => {
  const Icon = MEDIA_TYPE_ICONS[item.fileType] || FileQuestion;
  const [previewFailed, setPreviewFailed] = useState(false);
  const isImage = item.fileType === 'image';
  const isVideoLike = item.fileType === 'video' || item.fileType === 'session_recording';
  const showRealPreview = (isImage || isVideoLike) && !previewFailed;

  return (
    <div
      onClick={() => onOpen(item)}
      className="group relative bg-base-100 border border-base-content/10 hover:border-primary/40 rounded-xl overflow-hidden cursor-pointer transition-all hover:shadow-md"
    >
      <div className="aspect-[4/3] bg-base-200/60 relative overflow-hidden flex items-center justify-center">
        {showRealPreview ? (
          isImage ? (
            <img
              src={item.fileUrl}
              alt={item.fileName}
              className="w-full h-full object-cover"
              onError={() => setPreviewFailed(true)}
            />
          ) : (
            <>
              <video
                src={item.fileUrl}
                muted
                preload="metadata"
                className="w-full h-full object-cover"
                onError={() => setPreviewFailed(true)}
              />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/10">
                <div className="w-6 h-6 rounded-full bg-black/50 flex items-center justify-center text-white">
                  <Play size={10} className="fill-white ml-0.5" />
                </div>
              </div>
            </>
          )
        ) : (
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon size={14} className="text-primary" />
          </div>
        )}

        {/* Quick Preview — keyboard/assistive-tech accessible equivalent of the card's onClick */}
        <div className="tooltip tooltip-top tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap absolute top-1 left-1" data-tip="Preview media">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpen(item); }}
            aria-label="Preview media"
            className="p-1 rounded-md bg-base-100/90 text-base-content/60 hover:text-primary hover:bg-primary/10 opacity-80 group-hover:opacity-100 group-focus-within:opacity-100 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
          >
            <Eye size={12} />
          </button>
        </div>

        {/* Quick Remove — visible by default (dimmed), full opacity on hover/keyboard focus */}
        {canRemove && (
          <div className="tooltip tooltip-top tooltip-error before:text-xs before:font-semibold before:whitespace-nowrap absolute top-1 right-1" data-tip="Remove media">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onRemove(item); }}
              aria-label="Remove media"
              className="p-1 rounded-md bg-base-100/90 text-base-content/60 hover:text-error hover:bg-error/10 opacity-80 group-hover:opacity-100 group-focus-within:opacity-100 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error focus-visible:ring-offset-1"
            >
              <Trash2 size={12} />
            </button>
          </div>
        )}
      </div>

      <div className="p-2 space-y-0.5">
        <div className="tooltip tooltip-bottom tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap block w-full" data-tip={item.fileName}>
          <p className="text-[11px] font-bold text-base-content truncate">
            {item.fileName}
          </p>
        </div>
        <div className="flex items-center justify-between text-[9px] text-base-content/40 font-bold uppercase tracking-wider">
          <span>{formatFileSize(item.fileSize)}</span>
          {item.requestNumber ? (
            <div className="tooltip tooltip-top tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap" data-tip={`View ${SERVICE_REQUEST_LABEL.singular} ${item.requestNumber}`}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (item.serviceRequestUid) {
                    window.open(`/service-requests/${item.serviceRequestUid}`, '_blank', 'noopener,noreferrer');
                  }
                }}
                className="inline-flex items-center gap-0.5 text-primary hover:underline truncate max-w-[110px]"
              >
                <span className="truncate">{item.requestNumber}</span>
                <ExternalLink size={9} className="flex-shrink-0" />
              </button>
            </div>
          ) : (
            <span className="text-base-content/30 italic">—</span>
          )}
        </div>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* List / Table Row Component                                                 */
/* -------------------------------------------------------------------------- */
const MediaTableRow = ({
  item,
  onOpen,
  canRemove,
  onRemove,
}) => {
  const Icon = MEDIA_TYPE_ICONS[item.fileType] || FileQuestion;
  const [previewFailed, setPreviewFailed] = useState(false);
  const isImage = item.fileType === 'image';
  const isVideoLike = item.fileType === 'video' || item.fileType === 'session_recording';
  const showRealPreview = (isImage || isVideoLike) && !previewFailed;

  return (
    <tr
      onClick={() => onOpen(item)}
      className="border-b border-base-content/5 cursor-pointer transition-colors group hover:bg-base-200/50"
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-9 rounded-lg bg-base-200 overflow-hidden flex items-center justify-center flex-shrink-0 relative border border-base-content/10">
            {showRealPreview ? (
              isImage ? (
                <img
                  src={item.fileUrl}
                  alt={item.fileName}
                  className="w-full h-full object-cover"
                  onError={() => setPreviewFailed(true)}
                />
              ) : (
                <>
                  <video
                    src={item.fileUrl}
                    muted
                    preload="metadata"
                    className="w-full h-full object-cover"
                    onError={() => setPreviewFailed(true)}
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <Play size={8} className="fill-white text-white" />
                  </div>
                </>
              )
            ) : (
              <Icon size={14} className="text-primary" />
            )}
          </div>
          <div className="min-w-0">
            <p className="text-xs sm:text-sm font-bold text-base-content truncate max-w-xs sm:max-w-md" title={item.fileName}>
              {item.fileName}
            </p>
            <p className="text-[10px] text-base-content/40 font-mono truncate">{item.uid}</p>
          </div>
        </div>
      </td>

      <td className="px-4 py-3 whitespace-nowrap">
        <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-base-200 text-base-content/70">
          {SERVICE_REQUEST_MEDIA_FILE_TYPE_LABELS[item.fileType] || item.fileType}
        </span>
      </td>

      <td className="px-4 py-3 whitespace-nowrap text-xs font-bold text-base-content/70">
        {formatFileSize(item.fileSize)}
      </td>

      <td className="px-4 py-3 whitespace-nowrap">
        {item.requestNumber ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              window.open(`/service-requests/${item.serviceRequestUid}`, '_blank', 'noopener,noreferrer');
            }}
            className="inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline"
          >
            <span>{item.requestNumber}</span>
            <ExternalLink size={11} />
          </button>
        ) : (
          <span className="text-xs text-base-content/30 italic">—</span>
        )}
      </td>

      <td className="px-4 py-3 whitespace-nowrap text-xs text-base-content/50 font-medium">
        {formatRelativeTime(item.createdAt)}
      </td>

      <td className="px-4 py-3 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-1 opacity-80 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={() => onOpen(item)}
            className="p-1.5 rounded-lg hover:bg-base-300 text-base-content/60 hover:text-base-content transition"
            title="Preview"
          >
            <Eye size={15} />
          </button>
          {canRemove && (
            <button
              type="button"
              onClick={() => onRemove(item)}
              className="p-1.5 rounded-lg hover:bg-error/10 text-base-content/60 hover:text-error transition"
              title="Remove media"
            >
              <Trash2 size={15} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

/* -------------------------------------------------------------------------- */
/* Main MediaLibrary Component                                                */
/* -------------------------------------------------------------------------- */
const MediaLibrary = () => {
  const { user } = useAuth();
  const { can } = usePermissions();
  const canRemove = can.service_requests.deleteMedia();

  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(null);
  const [countFileType, setCountFileType] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // View Mode: comfortable | compact | list
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('media_library_view_mode') || 'comfortable';
  });

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem('media_library_view_mode', mode);
  };

  // Filters
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [debouncedFilters, setDebouncedFilters] = useState(INITIAL_FILTERS);
  const [accounts, setAccounts] = useState([]);
  const [subAccounts, setSubAccounts] = useState([]);
  const showAccountFilter = !user?.accountUID;
  const canViewSubAccounts = can.sub_accounts.view();
  const showSubAccountFilter = !user?.subAccountUID && canViewSubAccounts;
  const effectiveAccountId = filters.accountUid || user?.accountUID || '';
  const subAccountDisabled = showAccountFilter && !filters.accountUid;

  // Modals
  const [viewerItem, setViewerItem] = useState(null);
  const [removeMediaTarget, setRemoveMediaTarget] = useState(null);
  const [removingMedia, setRemovingMedia] = useState(false);

  const debouncedFiltersRef = useRef(debouncedFilters);
  useEffect(() => { debouncedFiltersRef.current = debouncedFilters; }, [debouncedFilters]);

  // Guards against a slower, older fetchPage call resolving after a newer one
  // and overwriting its results (e.g. rapid filter changes or overlapping loads).
  const fetchRequestIdRef = useRef(0);

  // Load Accounts
  useEffect(() => {
    if (user?.accountUID) return;
    const fetchAccounts = async () => {
      try {
        const { data } = await accountService.getAccounts({ limit: 100 });
        setAccounts(sortByName(data.data || data || []));
      } catch {
        toastService.error(`Failed to load ${ACCOUNT_LABEL.pluralLower}`);
      }
    };
    fetchAccounts();
  }, [user?.accountUID]);

  // Load Sub-Accounts
  useEffect(() => {
    if (!showSubAccountFilter) return;
    if (!effectiveAccountId) {
      setSubAccounts([]);
      return;
    }
    const fetchSubAccounts = async () => {
      try {
        const { data } = await subAccountService.getSubAccounts(effectiveAccountId, { limit: 100 });
        setSubAccounts(sortByName(data.data || []));
      } catch {
        toastService.error(`Failed to load ${SUB_ACCOUNT_LABEL.pluralLower}`);
      }
    };
    fetchSubAccounts();
  }, [effectiveAccountId, showSubAccountFilter]);

  // Reset Sub-Account filter when Account changes
  useEffect(() => {
    setFilters((f) => (f.subAccountUid ? { ...f, subAccountUid: '' } : f));
  }, [filters.accountUid]);

  // Debounce search/filters
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilters(filters), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters]);

  // Fetch Items from Backend. `offset` is the row offset to request — always
  // derived from how many items are actually loaded client-side, so it stays
  // correct even after a deletion shrinks the loaded set (see handleRemoveMedia).
  const fetchPage = useCallback(async (offset, replace = false) => {
    const requestId = ++fetchRequestIdRef.current;
    const isCurrent = () => fetchRequestIdRef.current === requestId;
    try {
      if (replace) {
        setLoading(true);
        setTotalCount(null); // Clear previous count so stale count is not displayed
        setCountFileType(null);
      } else {
        setLoadingMore(true);
      }
      const currentFilters = debouncedFiltersRef.current;
      const { data } = await mediaLibraryService.getMediaItems({
        offset: replace ? 0 : offset,
        limit: DEFAULT_LIMIT,
        fileType: currentFilters.fileType === 'all' ? undefined : currentFilters.fileType,
        accountUid: currentFilters.accountUid || undefined,
        subAccountUid: currentFilters.subAccountUid || undefined,
        fileName: currentFilters.fileName || undefined,
      });
      if (!isCurrent()) { return; }
      const rows = data.data || [];
      const total = data.total ?? 0;
      setTotalCount(total);
      setCountFileType(currentFilters.fileType);
      setItems((prev) => {
        const next = replace ? rows : [...prev, ...rows];
        setHasMore(rows.length > 0 && next.length < total);
        return next;
      });
    } catch (err) {
      if (!isCurrent()) { return; }
      toastService.error(err.response?.data?.message || 'Failed to load media library');
    } finally {
      if (isCurrent()) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchPage(0, true);
  }, [debouncedFilters, fetchPage]);

  const handleLoadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    fetchPage(items.length, false);
  }, [fetchPage, hasMore, items.length, loading, loadingMore]);

  const sentinelRef = useInfiniteScroll({
    onLoadMore: handleLoadMore,
    hasMore,
    disabled: loading || loadingMore,
  });

  const handleRemoveMedia = async () => {
    if (!removeMediaTarget) return;
    try {
      setRemovingMedia(true);
      await serviceRequestService.removeMediaItem(removeMediaTarget.serviceRequestUid, removeMediaTarget.uid);
      toastService.success('Media item removed successfully');
      setItems((prev) => prev.filter((i) => i.uid !== removeMediaTarget.uid));
      setTotalCount((prev) => (prev !== null ? Math.max(0, prev - 1) : prev));
      setRemoveMediaTarget(null);
    } catch (err) {
      toastService.error(err.response?.data?.message || 'Failed to remove media item');
    } finally {
      setRemovingMedia(false);
    }
  };

  const hasActiveFilters =
    filters.fileType !== 'all' ||
    filters.accountUid !== '' ||
    filters.subAccountUid !== '' ||
    filters.fileName !== '';

  const handleClearFilters = () => {
    setFilters(INITIAL_FILTERS);
  };

  return (
    <div className="w-full space-y-4 pb-2">
      {/* Hero Header */}
      <div className="glass-card rounded-2xl p-4 sm:p-5 shadow-sm border border-base-content/10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight">
              Media Library
            </h1>
            <p className="text-xs text-base-content/50">
              Browse, search, and inspect media across all {SERVICE_REQUEST_LABEL.pluralLower} in your scope.
            </p>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap">
            {/* View Mode Switcher */}
            <div className="flex items-center bg-base-200/80 p-1 rounded-xl border border-base-content/5">
              <button
                type="button"
                onClick={() => handleViewModeChange('comfortable')}
                className={`p-1.5 px-2.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                  viewMode === 'comfortable'
                    ? 'bg-base-100 text-primary shadow-sm'
                    : 'text-base-content/50 hover:text-base-content'
                }`}
                title="Comfortable Grid View"
              >
                <LayoutGrid size={15} />
                <span className="hidden sm:inline text-xs font-bold">Grid</span>
              </button>
              <button
                type="button"
                onClick={() => handleViewModeChange('compact')}
                className={`p-1.5 px-2.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                  viewMode === 'compact'
                    ? 'bg-base-100 text-primary shadow-sm'
                    : 'text-base-content/50 hover:text-base-content'
                }`}
                title="Compact Grid View"
              >
                <Grid size={15} />
                <span className="hidden sm:inline text-xs font-bold">Compact</span>
              </button>
              <button
                type="button"
                onClick={() => handleViewModeChange('list')}
                className={`p-1.5 px-2.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                  viewMode === 'list'
                    ? 'bg-base-100 text-primary shadow-sm'
                    : 'text-base-content/50 hover:text-base-content'
                }`}
                title="Table / List View"
              >
                <List size={15} />
                <span className="hidden sm:inline text-xs font-bold">List</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filter & Search Bar Card */}
      <div className="glass-card rounded-2xl p-3 sm:p-4 shadow-sm border border-base-content/10 space-y-3 relative z-30">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          {/* Left: File Type Quick Filter Tabs */}
          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-0.5 flex-shrink-0">
            <button
              type="button"
              onClick={() => setFilters((f) => ({ ...f, fileType: 'all' }))}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex-shrink-0 ${
                filters.fileType === 'all'
                  ? 'bg-primary text-primary-content shadow-sm'
                  : 'bg-base-100 border border-base-300 text-base-content/70 hover:bg-base-200'
              }`}
            >
              <span>All Media</span>
              {filters.fileType === 'all' && !loading && totalCount !== null && countFileType === 'all' && (
                <span className="px-1.5 py-0.2 rounded-md text-[10px] font-black bg-primary-content/20 text-primary-content animate-fade-in">
                  {totalCount}
                </span>
              )}
            </button>

            {SERVICE_REQUEST_MEDIA_FILE_TYPES.map((type) => {
              const Icon = MEDIA_TYPE_ICONS[type] || FileQuestion;
              const isSelected = filters.fileType === type;
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => setFilters((f) => ({ ...f, fileType: type }))}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all flex-shrink-0 ${
                    isSelected
                      ? 'bg-primary text-primary-content shadow-sm'
                      : 'bg-base-100 border border-base-300 text-base-content/70 hover:bg-base-200'
                  }`}
                >
                  <Icon size={14} />
                  <span>{SERVICE_REQUEST_MEDIA_FILE_TYPE_LABELS[type]}</span>
                  {isSelected && !loading && totalCount !== null && countFileType === type && (
                    <span className="px-1.5 py-0.2 rounded-md text-[10px] font-black bg-primary-content/20 text-primary-content animate-fade-in">
                      {totalCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Right: Search, Account & Sub-Account Controls */}
          <div className="flex items-center gap-2.5 flex-1 lg:flex-initial lg:justify-end flex-wrap sm:flex-nowrap min-w-0">
            {/* Search Input */}
            <div className="relative flex-1 sm:w-56 md:w-64">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-base-content/40" size={15} />
              <input
                type="text"
                placeholder="Search by file name..."
                className="w-full pl-9 pr-8 py-2 bg-base-100 border border-base-300 rounded-xl text-xs sm:text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium"
                value={filters.fileName}
                onChange={(e) => setFilters((f) => ({ ...f, fileName: e.target.value }))}
              />
              {filters.fileName && (
                <button
                  type="button"
                  onClick={() => setFilters((f) => ({ ...f, fileName: '' }))}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-base-content/30 hover:text-base-content"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Account Filter (if visible) */}
            {showAccountFilter && (
              <div className="w-full sm:w-44 flex-shrink-0">
                <select
                  className="w-full px-3 py-2 bg-base-100 border border-base-300 rounded-xl text-xs sm:text-sm text-base-content focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium"
                  value={filters.accountUid}
                  onChange={(e) => setFilters((f) => ({ ...f, accountUid: e.target.value, subAccountUid: '' }))}
                >
                  <option value="">All {ACCOUNT_LABEL.plural}</option>
                  {accounts.map((acc) => (
                    <option key={acc.id} value={acc.id}>{acc.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Sub Account Filter (if visible) */}
            {showSubAccountFilter && (
              <div className="w-full sm:w-44 flex-shrink-0">
                <select
                  className={`w-full px-3 py-2 bg-base-100 border border-base-300 rounded-xl text-xs sm:text-sm text-base-content focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium ${
                    subAccountDisabled ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  value={filters.subAccountUid}
                  disabled={subAccountDisabled}
                  title={subAccountDisabled ? `Select ${ACCOUNT_LABEL.withArticle} first` : undefined}
                  onChange={(e) => setFilters((f) => ({ ...f, subAccountUid: e.target.value }))}
                >
                  <option value="">{subAccountDisabled ? `Select ${ACCOUNT_LABEL.singular} First` : `All ${SUB_ACCOUNT_LABEL.plural}`}</option>
                  {subAccounts.map((sub) => (
                    <option key={sub.id} value={sub.id}>{sub.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Active Filter Badges Bar */}
        {hasActiveFilters && (
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-base-content/5 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] font-black uppercase tracking-wider text-base-content/40 flex items-center gap-1 mr-1">
                <Filter size={11} />
                <span>Active:</span>
              </span>

              {filters.fileName && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  <span>Search: "{filters.fileName}"</span>
                  <button
                    type="button"
                    onClick={() => setFilters((f) => ({ ...f, fileName: '' }))}
                    className="hover:text-error transition-colors"
                  >
                    <X size={12} />
                  </button>
                </span>
              )}

              {filters.fileType !== 'all' && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  <Tag size={10} className="text-primary" />
                  <span>Type: {SERVICE_REQUEST_MEDIA_FILE_TYPE_LABELS[filters.fileType] || filters.fileType}</span>
                  <button
                    type="button"
                    onClick={() => setFilters((f) => ({ ...f, fileType: 'all' }))}
                    className="hover:text-error transition-colors"
                  >
                    <X size={12} />
                  </button>
                </span>
              )}

              {filters.accountUid && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  <Building2 size={10} className="text-primary" />
                  <span>{ACCOUNT_LABEL.singular}: {accounts.find((a) => a.id === filters.accountUid)?.name || filters.accountUid}</span>
                  <button
                    type="button"
                    onClick={() => setFilters((f) => ({ ...f, accountUid: '' }))}
                    className="hover:text-error transition-colors"
                  >
                    <X size={12} />
                  </button>
                </span>
              )}

              {filters.subAccountUid && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  <Layers size={10} className="text-primary" />
                  <span>{SUB_ACCOUNT_LABEL.singular}: {subAccounts.find((s) => s.id === filters.subAccountUid)?.name || filters.subAccountUid}</span>
                  <button
                    type="button"
                    onClick={() => setFilters((f) => ({ ...f, subAccountUid: '' }))}
                    className="hover:text-error transition-colors"
                  >
                    <X size={12} />
                  </button>
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={handleClearFilters}
              className="text-xs font-bold text-base-content/40 hover:text-error transition-colors ml-auto"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* -------------------------------------------------------------------- */}
      {/* Media Content Display Area                                           */}
      {/* -------------------------------------------------------------------- */}
      <div>
        {loading ? (
          viewMode === 'list' ? (
            <div className="overflow-x-auto glass-panel rounded-2xl border border-base-content/5">
              <table className="table w-full">
                <tbody>
                  {Array.from({ length: 8 }).map((_, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <MediaCardSkeleton key={i} viewMode="list" />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={`grid gap-4 ${viewMode === 'compact'
              ? 'grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8'
              : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'
              }`}>
              {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <MediaCardSkeleton key={i} viewMode={viewMode} />
              ))}
            </div>
          )
        ) : items.length === 0 ? (
          /* Empty State */
          <div className="text-center py-24 glass-panel !bg-base-100/40 rounded-3xl border-dashed border-2 border-base-content/10 p-8 space-y-4">
            <div className="w-20 h-20 bg-primary/10 rounded-3xl flex items-center justify-center mx-auto shadow-inner text-primary">
              <FolderOpen size={36} />
            </div>
            <div className="space-y-1 max-w-sm mx-auto">
              <h3 className="text-xl font-black text-base-content tracking-tight">No media items found</h3>
              <p className="text-xs text-base-content/60 font-medium">
                {hasActiveFilters
                  ? 'No media matches your current search or filter combination.'
                  : 'Your media library is currently empty.'}
              </p>
            </div>
            {hasActiveFilters && (
              <div className="pt-2 flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="btn btn-sm btn-ghost border border-base-content/10 rounded-xl font-bold text-xs"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        ) : (
          /* Render Active View Mode */
          <>
            {viewMode === 'list' ? (
              <div className="overflow-x-auto glass-panel rounded-2xl border border-base-content/5">
                <table className="table w-full">
                  <thead>
                    <tr className="border-b border-base-content/5">
                      <th className="bg-transparent text-[10px] font-black uppercase tracking-widest text-base-content/40 py-4">File Name</th>
                      <th className="bg-transparent text-[10px] font-black uppercase tracking-widest text-base-content/40 py-4">Type</th>
                      <th className="bg-transparent text-[10px] font-black uppercase tracking-widest text-base-content/40 py-4">Size</th>
                      <th className="bg-transparent text-[10px] font-black uppercase tracking-widest text-base-content/40 py-4">{SERVICE_REQUEST_LABEL.singular}</th>
                      <th className="bg-transparent text-[10px] font-black uppercase tracking-widest text-base-content/40 py-4">Date Added</th>
                      <th className="bg-transparent text-[10px] font-black uppercase tracking-widest text-base-content/40 py-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <MediaTableRow
                        key={item.uid}
                        item={item}
                        onOpen={setViewerItem}
                        canRemove={canRemove}
                        onRemove={setRemoveMediaTarget}
                      />
                    ))}
                    {loadingMore && Array.from({ length: 3 }).map((_, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <MediaCardSkeleton key={`more-${i}`} viewMode="list" />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : viewMode === 'compact' ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                {items.map((item) => (
                  <CompactMediaCard
                    key={item.uid}
                    item={item}
                    onOpen={setViewerItem}
                    canRemove={canRemove}
                    onRemove={setRemoveMediaTarget}
                  />
                ))}
                {loadingMore && Array.from({ length: 6 }).map((_, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <MediaCardSkeleton key={`more-${i}`} viewMode="compact" />
                ))}
              </div>
            ) : (
              /* Comfortable Grid */
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {items.map((item) => (
                  <ComfortableMediaCard
                    key={item.uid}
                    item={item}
                    onOpen={setViewerItem}
                    canRemove={canRemove}
                    onRemove={setRemoveMediaTarget}
                  />
                ))}
                {loadingMore && Array.from({ length: 4 }).map((_, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <MediaCardSkeleton key={`more-${i}`} viewMode="comfortable" />
                ))}
              </div>
            )}

            {/* Infinite Scroll Sentinel & End Indicator */}
            {hasMore ? (
              <div ref={sentinelRef} className="py-6 flex items-center justify-center">
                {loadingMore && (
                  <div className="flex items-center gap-2 text-xs font-bold text-base-content/50 bg-base-100 px-4 py-2 rounded-full border border-base-content/10 shadow-sm">
                    <div className="w-3.5 h-3.5 border-2 border-primary/40 border-t-primary rounded-full animate-spin" />
                    <span>Loading more media...</span>
                  </div>
                )}
              </div>
            ) : items.length > 0 ? (
              <div className="text-center py-8">
                <span className="text-[11px] font-bold text-base-content/30 uppercase tracking-widest bg-base-100/60 px-4 py-1.5 rounded-full border border-base-content/5">
                  Showing all {totalCount !== null ? totalCount : items.length} {(totalCount ?? items.length) === 1 ? 'media item' : 'media items'}
                </span>
              </div>
            ) : null}
          </>
        )}
      </div>

      {/* -------------------------------------------------------------------- */}
      {/* Modal: Media Viewer Modal                                            */}
      {/* -------------------------------------------------------------------- */}
      <MediaViewerModal
        item={viewerItem}
        onClose={() => setViewerItem(null)}
      />

      {/* -------------------------------------------------------------------- */}
      {/* Modal: Remove Media Confirmation                                     */}
      {/* -------------------------------------------------------------------- */}
      <Modal isOpen={!!removeMediaTarget} onClose={() => setRemoveMediaTarget(null)} title="Remove Media Item">
        <div className="mb-6">
          <p className="text-base-content/60 text-sm leading-relaxed">
            Are you sure you want to remove{' '}
            <strong className="text-base-content">{removeMediaTarget?.fileName}</strong>?
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setRemoveMediaTarget(null)}
            className="flex-1 px-4 py-2.5 border border-base-content/20 text-base-content/70 rounded-xl font-bold text-sm hover:bg-base-200 transition"
            disabled={removingMedia}
          >
            Cancel
          </button>
          <button
            onClick={handleRemoveMedia}
            disabled={removingMedia}
            className="flex-1 px-4 py-2.5 bg-error text-error-content rounded-xl font-bold text-sm hover:opacity-90 transition disabled:opacity-50"
          >
            {removingMedia ? 'Removing...' : 'Remove'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default MediaLibrary;
